<template>
  <div id="reports">
    <Page :logo=imageBase64Data>
      <CoverPage
        :company-name="regionName"
        :month="month !== null ? month.format('MMMM YYYY') : ''"
        :sub-title="customTexts.subTitle"
        :date="today"
        :version="customTexts.version"
      />
      <div class="row edit">
        <div class="row">
          <span class="col s2">region:</span>
          <select name="region" id="region" v-model="regionId" class="browser-default col s5">
            <option v-for="region in regions" :value="region.RegionId" :key="region.RegionId">
              {{ region.Name }}
            </option>
          </select>
        </div>
        <div class="row">
          <span class="col s2">complex:</span>
          <select name="complex" id="complex" v-model="complexId" class="browser-default col s5">
            <option :value="null">
              -- {{ $t('views.switch.all') }} --
            </option>
            <option v-for="complex in complexes" :value="complex.ComplexId" :key="complex.ComplexId">
              {{ complex.Name }}
            </option>
          </select>
        </div>
        <div class="row">
          <span class="col s2">{{ $t('report.pickMonth') }}:</span>
          <VueMonthlyPicker
            v-model="month"
            :month-labels="monthLabels"
            dateFormat="MMMM YYYY"
            inputClass="select"
            :max="thisMonth"
            :placeHolder="$t('report.pickMonth')"
            :class="['col', 's5', { 'error': month === null }]"
          />
        </div>
        <div class="row">
          <span class="col s2">{{ $t('report.subtitle') }}:</span>
          <input type="text" v-model="customTexts.subTitle" class="col s5" style="background-color: white"/>
        </div>
          <div class="row">
        <span class="col s2">{{ $t('report.numMonths') }}:</span>
        <input
          type="number"
          min="1"
          max="6"
          step="1"
          v-model.number="apiData.params.numMonths"
          class="col s1"
          style="background-color: white"/>
          </div>
        <div class="row">
        <span class="col s2">{{ $t('report.numObjects') }}:</span>
        <input
          type="number"
          min="1"
          max="20"
          step="1"
          v-model.number="apiData.params.top"
          class="col s1"
          style="background-color: white"/>
      </div>
        <div class="row">
          <span class="col s2">{{ $t('report.version')}}:</span>
          <input type="text" v-model="customTexts.version" class="col s5" style="background-color: white"/>
          <div class="col s2 offset-s5">
            <button
              :disabled="chartDataLoading || month == null"
              type="button"
              class="right waves-effect waves-light btn buttonbg grey"
              @click="getChartData"
            >
              {{ $t('ok') }}
            </button>
          </div>
        </div>
      </div>
      <div class="progress" v-if="chartDataLoading">
        <div class="indeterminate"></div>
      </div>
    </Page>
    <div v-if="!chartDataLoading && !reportHasData && !noReportsGeneratedYet">
      <PageTitle :text="$t('events.noData')"/>
    </div>

    <Page v-if="!chartDataLoading && reportHasData">
      <PageTitle :text="$t('report.managementSummary')"/>
      <div class="row edit">
        <textarea
          name="managementText"
          id="managementText"
          v-model="customTexts.managementText"
          :placeholder="$t('report.generalConclusions')"
          cols="100"
          rows="10"
          style="background-color: white; height: 100px">
        </textarea>
      </div>
    </Page>

    <Page v-if="!chartDataLoading && reportHasData">
      <PageTitle :text="$t('report.switchStatistics')"/>
      <div id="pageoverviewchart">
        <h6>{{ $t('report.overviewSwitches') }}</h6>
        <div class="graph">
          <HighChart :options="charts.overview" ref="overviewChart"/>
        </div>
      </div>
      <div class="row edit">
        <textarea
          name="overviewText"
          id="overviewText"
          v-model="customTexts.overviewText"
          :placeholder="$t('report.generalConclusions')"
          cols="100"
          rows="10"
          style="background-color: white; height: 100px">
        </textarea>
      </div>
    </Page>

    <Page v-if="!chartDataLoading && reportHasData">
      <div id="pageerrorschart">
        <h6>{{ $t('report.overviewErrors') }}</h6>
        <div class="graph">
          <HighChart :options="charts.errors" ref="errorsChart"/>
        </div>
      </div>
      <div class="row edit">
        <textarea
          name="errorsText"
          id="errorsText"
          v-model="customTexts.errorsText"
          :placeholder="$t('report.generalConclusions')"
          cols="100"
          rows="10"
          style="background-color: white; height: 100px">
        </textarea>
      </div>
    </Page>

    <Page v-if="!chartDataLoading && reportHasData">
      <div id="pageanomalychart">
        <h6>{{ $t('report.anomalies')}}</h6>
        <div class="graph">
          <HighChart :options="charts.anomaly" ref="anomalyChart"/>
        </div>
      </div>
      <div class="row edit">
        <textarea
          name="anomalyText"
          id="anomalyText"
          v-model="customTexts.anomalyText"
          :placeholder="$t('report.generalConclusions')"
          cols="100"
          rows="10"
          style="background-color: white; height: 100px">
        </textarea>
      </div>
    </Page>

    <Page v-if="!chartDataLoading && reportHasData">
      <PageTitle :text="$t('report.switchAnalysis')"/>
      <div id="pagetop3chart">
        <h6>Top {{ apiData.params.top3 }} {{ $t('report.anomalies') }}</h6>
        <div class="graph">
          <HighChart :options="charts.top3" ref="top3Chart"/>
        </div>
      </div>
      <div class="row edit">
        <textarea
          name="top3Text"
          id="top3Text"
          v-model="customTexts.top3Text"
          :placeholder="$t('report.generalConclusions')"
          cols="100"
          rows="10"
          style="background-color: white; height: 100px">
        </textarea>
      </div>
    </Page>
    <div v-if="customTexts.customPages !== [] && !chartDataLoading && reportHasData">
      <Page v-for="(value, index) in customTexts.customPages" :key="index">
        <PageTitle :text="value.title"/>
        <div v-if="value.text">{{ value.text }}</div>
        <div class="row edit">
          <div class="row">
            <span class="col s2">{{ $t('report.title') }}:</span>
            <i class="material-icons"
               style="cursor: pointer"
               @click="removeCustomPage(index)">
              clear
            </i>
            <input type="text" v-model="value.title" class="col s5" style="background-color: white"/>
            <textarea
              name="text"
              :id="'text' + index"
              v-model="value.text"
              cols="100"
              rows="10"
              style="background-color: white; height: 100px">
            </textarea>
          </div>
        </div>
      </Page>
    </div>
    <button v-if="!chartDataLoading && reportHasData"
      @click="generateDoc"
      :disabled="month === null"
      class="waves-effect waves-light btn buttonbg">
      <span v-if="month === null" style="color: red;">
        {{ $t('report.pickMonth') }}
      </span>
      <span v-else>
        {{ $t('report.generateDoc') }}
      </span>
    </button>
  </div>
</template>

<script>

import {Chart} from 'highcharts-vue'
import More from 'highcharts/highcharts-more'
import Highcharts from 'highcharts'
import loadExporting from 'highcharts/modules/exporting'
import loadExportData from 'highcharts/modules/export-data'

import VueMonthlyPicker from 'vue-monthly-picker'
import moment from 'moment'
import html2canvas from 'html2canvas'

import { Document, Packer, Paragraph, Header, Footer, Media, AlignmentType, HeadingLevel, TableOfContents, PageNumberFormat, TextRun, PageNumber } from 'docx'

import Page from '../../components/reportItems/Page'
import PageTitle from '../../components/reportItems/PageTitle'
import CoverPage from '../../components/reportItems/CoverPage'
import RegionService from '../../services/RegionService'
import ReportsApi from '../../services/api/ReportsApi'
import ComplexService from '../../services/ComplexService'
import DepartmentService from '@/services/DepartmentService'
import AuthenticationService from '../../services/AuthenticationService'

More(Highcharts)
loadExporting(Highcharts)
loadExportData(Highcharts)

export default {
  name: 'SwitchReports',
  components: {Page, CoverPage, PageTitle, VueMonthlyPicker, HighChart: Chart},
  props: {},
  data () {
    return {
      currentUser: AuthenticationService.getUser(),
      creator: 'Strukton Control Centre, Strukton Rail Nederland BV',
      imageBase64Data: `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`, // strukton logo base64 encoded
      regionId: null,
      complexId: null,
      today: moment().format('DD MMM YYYY'),
      monthLabels: [
        this.$t('months.short.january'), this.$t('months.short.february'), this.$t('months.short.march'),
        this.$t('months.short.april'), this.$t('months.short.may'), this.$t('months.short.june'),
        this.$t('months.short.july'), this.$t('months.short.august'), this.$t('months.short.september'),
        this.$t('months.short.october'), this.$t('months.short.november'), this.$t('months.short.december')
      ],
      fullMonths: [
        this.$t('months.long.january'), this.$t('months.long.february'), this.$t('months.long.march'),
        this.$t('months.long.april'), this.$t('months.long.may'), this.$t('months.long.june'),
        this.$t('months.long.july'), this.$t('months.long.august'), this.$t('months.long.september'),
        this.$t('months.long.october'), this.$t('months.long.november'), this.$t('months.long.december')
      ],
      month: null,
      pages: [],
      chapters: [],
      customTexts: {
        subTitle: this.$t('report.subtitle'),
        version: '1',
        managementText: '',
        overviewText: '',
        errorsText: '',
        anomalyText: '',
        top3Text: '',
        customPages: []
      },
      apiData: {
        params: {
          numMonths: 3,
          top: 10,
          top3: 3
        },
        overview: [],
        overviewFormatted: [],
        errors: [],
        errorsFormatted: [],
        ratioTopList: [],
        errorsTopList: [],
        anomaliesTopList: []
      },
      chartScreenshotData: {
        overview: '',
        errors: '',
        anomaly: '',
        top3: ''
      },
      charts: {
        overview: {
          chart: {
            type: 'column',
            zoomType: 'xy',
            height: 500,
            zooming: {
              mouseWheel: {
                enabled: true
              }
            },
            events: {
              load () {
                const chart = this

                // Block scroll wheel unless Shift is held
                chart._wheelBlocker = (e) => {
                  if (!e.shiftKey) {
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                    e.stopPropagation()
                  }
                }
                chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

                // Show zoom cursor when Shift is held
                chart._cursorHint = (e) => {
                  chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
                }
                chart.container.addEventListener('mousemove', chart._cursorHint)

                // Flip cursor immediately on Shift press/release
                chart._onKeyShift = (e) => {
                  if (e.key === 'Shift' && chart.container) {
                    chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                  }
                }
                chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
                chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
              },
              destroy () {
                const el = this.container
                if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
                if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
                if (this._offKeyDown) this._offKeyDown()
                if (this._offKeyUp) this._offKeyUp()
              }
            }
          },
          exporting: {
            showTable: true
          },
          title: {
            text: ''
          },
          credits: {
            enabled: false
          },
          accessibility: {
            enabled: false
          },
          subtitle: {
            text: ''
          },
          legend: {
            enabled: true
          },
          xAxis: {
            title: {
              text: ''
            },
            type: 'linear'
          },
          yAxis: {
            title: {
              text: ''
            }
          },
          plotOptions: {
            column: {
              borderWidth: 0,
              groupPadding: 0,
              shadow: false,
              dataLabels: {
                enabled: true
              }
            }
          },
          tooltip: {
            enabled: true
          },
          series: [
            {
              marker: {
                enabled: false
              },
              data: []
            }
          ]
        },
        errors: {
          chart: {
            type: 'column',
            zoomType: 'xy',
            height: 500,
            zooming: {
              mouseWheel: {
                enabled: true
              }
            },
            events: {
              load () {
                const chart = this

                // Block scroll wheel unless Shift is held
                chart._wheelBlocker = (e) => {
                  if (!e.shiftKey) {
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                    e.stopPropagation()
                  }
                }
                chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

                // Show zoom cursor when Shift is held
                chart._cursorHint = (e) => {
                  chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
                }
                chart.container.addEventListener('mousemove', chart._cursorHint)

                // Flip cursor immediately on Shift press/release
                chart._onKeyShift = (e) => {
                  if (e.key === 'Shift' && chart.container) {
                    chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                  }
                }
                chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
                chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
              },
              destroy () {
                const el = this.container
                if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
                if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
                if (this._offKeyDown) this._offKeyDown()
                if (this._offKeyUp) this._offKeyUp()
              }
            }
          },
          exporting: {
            showTable: true
          },
          title: {
            text: ''
          },
          accessibility: {
            enabled: false
          },
          credits: {
            enabled: false
          },
          subtitle: {
            text: ''
          },
          legend: {
            enabled: true
          },
          xAxis: {
            title: {
              text: ''
            },
            type: 'linear'
          },
          yAxis: {
            title: {
              text: ''
            }
          },
          plotOptions: {
            column: {
              borderWidth: 0,
              groupPadding: 0,
              shadow: false,
              dataLabels: {
                enabled: true
              }
            }
          },
          tooltip: {
            enabled: true
          },
          series: [
            {
              marker: {
                enabled: false
              },
              data: []
            }
          ]
        },
        anomaly: {
          chart: {
            type: 'column',
            zoomType: 'xy',
            height: 500,
            zooming: {
              mouseWheel: {
                enabled: true
              }
            },
            events: {
              load () {
                const chart = this

                // Block scroll wheel unless Shift is held
                chart._wheelBlocker = (e) => {
                  if (!e.shiftKey) {
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                    e.stopPropagation()
                  }
                }
                chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

                // Show zoom cursor when Shift is held
                chart._cursorHint = (e) => {
                  chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
                }
                chart.container.addEventListener('mousemove', chart._cursorHint)

                // Flip cursor immediately on Shift press/release
                chart._onKeyShift = (e) => {
                  if (e.key === 'Shift' && chart.container) {
                    chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                  }
                }
                chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
                chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
              },
              destroy () {
                const el = this.container
                if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
                if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
                if (this._offKeyDown) this._offKeyDown()
                if (this._offKeyUp) this._offKeyUp()
              }
            }
          },
          exporting: {
            showTable: true
          },
          title: {
            text: ''
          },
          credits: {
            enabled: false
          },
          accessibility: {
            enabled: false
          },
          subtitle: {
            text: ''
          },
          legend: {
            enabled: true
          },
          xAxis: {
            title: {
              text: ''
            },
            type: 'linear'
          },
          yAxis: {
            title: {
              text: ''
            }
          },
          plotOptions: {
            column: {
              borderWidth: 0,
              groupPadding: 0,
              shadow: false,
              dataLabels: {
                enabled: true
              }
            }
          },
          tooltip: {
            enabled: true
          },
          series: []
        },
        top3: {
          chart: {
            type: 'column',
            zoomType: 'xy',
            height: 500,
            zooming: {
              mouseWheel: {
                enabled: true
              }
            },
            events: {
              load () {
                const chart = this

                // Block scroll wheel unless Shift is held
                chart._wheelBlocker = (e) => {
                  if (!e.shiftKey) {
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                    e.stopPropagation()
                  }
                }
                chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

                // Show zoom cursor when Shift is held
                chart._cursorHint = (e) => {
                  chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
                }
                chart.container.addEventListener('mousemove', chart._cursorHint)

                // Flip cursor immediately on Shift press/release
                chart._onKeyShift = (e) => {
                  if (e.key === 'Shift' && chart.container) {
                    chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                  }
                }
                chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
                chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
              },
              destroy () {
                const el = this.container
                if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
                if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
                if (this._offKeyDown) this._offKeyDown()
                if (this._offKeyUp) this._offKeyUp()
              }
            }
          },
          exporting: {
            showTable: true
          },
          title: {
            text: ''
          },
          credits: {
            enabled: false
          },
          subtitle: {
            text: ''
          },
          accessibility: {
            enabled: false
          },
          legend: {
            enabled: true
          },
          xAxis: {
            title: {
              text: ''
            },
            type: 'linear'
          },
          yAxis: {
            title: {
              text: ''
            }
          },
          plotOptions: {
            column: {
              borderWidth: 0,
              groupPadding: 0,
              shadow: false,
              dataLabels: {
                enabled: true
              }
            }
          },
          tooltip: {
            enabled: true
          },
          series: []
        }
      },
      chartDataLoading: false,
      reportHasData: false,
      noReportsGeneratedYet: true
    }
  },
  methods: {
    async generateDoc () {
      await this.getScreenshotData()
      const selectedMonth = this.month.format('M') - 1
      const monthText = this.fullMonths[selectedMonth]
      const title = this.regionName + ' ' + this.$t('report.monthReport') + ' - ' + monthText + ' ' + this.month.format('YYYY')
      const doc = new Document(
        {
          creator: this.creator,
          title: this.title,
          description: 'Generated report',
          styles: {
            paragraphStyles: [
              {
                id: 'myHeading1',
                name: 'Heading 1',
                basedOn: 'Normal',
                next: 'Normal',
                quickFormat: true,
                heading: HeadingLevel.HEADING_1,
                pageBreakBefore: true,
                run: {
                  bold: true,
                  size: 32
                },
                paragraph: {
                  spacing: {
                    before: 240,
                    after: 120
                  }
                }
              },
              {
                id: 'myHeading2',
                name: 'Heading 2',
                basedOn: 'Normal',
                next: 'Normal',
                quickFormat: true,
                heading: HeadingLevel.HEADING_2,
                pageBreakBefore: false,
                run: {
                  bold: true,
                  size: 26
                },
                paragraph: {
                  spacing: {
                    before: 240,
                    after: 120
                  }
                }
              },
              {
                id: 'myFP1',
                name: 'myFP1',
                basedOn: 'Normal',
                next: 'Normal',
                heading: HeadingLevel.TITLE,
                quickFormat: true,
                pageBreakBefore: false,
                run: {
                  bold: true,
                  size: 32
                },
                paragraph: {
                  spacing: {
                    before: 520,
                    after: 360
                  }
                }
              },
              {
                id: 'myFP2',
                name: 'myFP2',
                basedOn: 'Normal',
                next: 'Normal',
                quickFormat: true,
                pageBreakBefore: false,
                run: {
                  bold: true,
                  size: 24
                },
                paragraph: {
                  spacing: {
                    before: 520,
                    after: 360
                  }
                }
              },
              {
                id: 'myFP3',
                name: 'myFP3',
                basedOn: 'Normal',
                next: 'Normal',
                quickFormat: true,
                pageBreakBefore: false,
                run: {
                  bold: true,
                  size: 14
                },
                paragraph: {
                  spacing: {
                    before: 520,
                    after: 360
                  }
                }
              }
            ]
          }
        })
      const logo = Media.addImage(doc, Buffer.from(this.imageBase64Data, 'base64'), 120, 28)
      const overviewconv = Media.addImage(doc, Buffer.from(this.chartScreenshotData.overview.toString().replace('data:image/png;base64,', ''), 'base64'), 600, 450)
      const errorsconv = Media.addImage(doc, Buffer.from(this.chartScreenshotData.errors.toString().replace('data:image/png;base64,', ''), 'base64'), 600, 450)
      const anomalyconv = Media.addImage(doc, Buffer.from(this.chartScreenshotData.anomaly.toString().replace('data:image/png;base64,', ''), 'base64'), 600, 450)
      const top3conv = Media.addImage(doc, Buffer.from(this.chartScreenshotData.top3.toString().replace('data:image/png;base64,', ''), 'base64'), 600, 450)

      doc.addSection({
        properties: {
          pageNumberStart: 1,
          pageNumberFormatType: PageNumberFormat.DECIMAL
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph(
                logo
              ),
              new Paragraph({
                text: this.regionName + ' ' + this.$t('report.monthReport') + ' - ' + monthText + ' ' + this.month.format('YYYY'),
                alignment: AlignmentType.RIGHT
              })
            ]
          })
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ children: [PageNumber.CURRENT] }),
                  new TextRun({ text: '/' }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES] })
                ]
              })
            ]
          })
        },
        children: [
          new Paragraph({
            text: title,
            style: 'myFP1'
          }),
          new Paragraph({
            text: this.customTexts.subTitle,
            thematicBreak: true,
            style: 'myFP2'
          }),
          new Paragraph({
            text: this.creator,
            style: 'myFP3'
          }),
          new Paragraph({
            text: 'Created ' + this.today,
            style: 'myFP3'
          }),
          new Paragraph({
            text: 'Version ' + this.customTexts.version,
            style: 'myFP3'
          }),
          new Paragraph({
            text: this.$t('report.toc'),
            style: 'myHeading1',
            pageBreakBefore: true
          }),
          new TableOfContents('Summary',
            {
              hyperlink: true,
              headingStyleRange: '1-3',
              pageBreakBefore: true
            }),
          new Paragraph({
            text: this.$t('report.managementSummary'),
            style: 'myHeading1',
            pageBreakBefore: true
          }),
          new Paragraph({
            text: this.customTexts.managementText
          }),
          new Paragraph({
            text: this.$t('report.switchStatistics'),
            style: 'myHeading1',
            pageBreakBefore: true
          }),
          new Paragraph({
            text: this.$t('report.switchStatisticsText')
          }),
          new Paragraph({
            text: this.$t('report.overviewSwitches'),
            style: 'myHeading2'
          }),
          new Paragraph(
            overviewconv
          ),
          new Paragraph({
            text: this.customTexts.overviewText
          }),
          new Paragraph({
            text: this.$t('report.overviewErrors'),
            style: 'myHeading2'
          }),
          new Paragraph({
            text: this.$t('report.overviewErrorsText')
          }),
          new Paragraph(
            errorsconv
          ),
          new Paragraph({
            text: this.customTexts.errorsText
          }),
          new Paragraph({
            text: this.$t('report.anomalies'),
            style: 'myHeading2'
          }),
          new Paragraph({
            text: this.$t('report.anomaliesText', [this.apiData.params.top3])
          }),
          new Paragraph(
            anomalyconv
          ),
          new Paragraph({
            text: this.chartScreenshotData.anomalyText
          }),
          new Paragraph({
            text: this.$t('report.switchAnalysis'),
            style: 'myHeading1',
            pageBreakBefore: true
          }),
          new Paragraph({
            text: `Top ${this.apiData.params.top3} ${this.$t('report.anomalies')}`,
            style: 'myHeading2'
          }),
          new Paragraph({
            text: this.$t('report.overviewErrorsText')
          }),
          new Paragraph({
            text: [this.apiData.params.top3]
          }),
          new Paragraph(top3conv),
          new Paragraph({
            text: this.customTexts.top3Text
          })
        ]
      })

      // Used to export the file into a .docx file
      Packer.toBase64String(doc).then((buffer) => {
        var element = document.createElement('a')
        element.setAttribute('href', 'data:application/ms-doc;base64,' + encodeURIComponent(buffer))
        element.setAttribute('download', `${this.regionName}_${this.thisDateKey}_v${this.customTexts.version}.doc`)

        element.style.display = 'none'
        document.body.appendChild(element)

        element.click()

        document.body.removeChild(element)
      })
    },
    async getScreenshotData () {
      await html2canvas(document.getElementById('pageoverviewchart')).then(canvas => {
        this.chartScreenshotData.overview = canvas.toDataURL()
      })

      await html2canvas(document.getElementById('pageerrorschart')).then(canvas => {
        this.chartScreenshotData.errors = canvas.toDataURL()
      })

      await html2canvas(document.getElementById('pageanomalychart')).then(canvas => {
        this.chartScreenshotData.anomaly = canvas.toDataURL()
      })

      await html2canvas(document.getElementById('pagetop3chart')).then(canvas => {
        this.chartScreenshotData.top3 = canvas.toDataURL()
      })
    },
    async getChartData () {
      // api calls:
      let getReportOverviewHasData = false
      let getReportErrorsHasData = false
      this.noReportsGeneratedYet = false
      try {
        this.chartDataLoading = true

        if (this.complexId) {
          this.apiData.overview = (await ReportsApi.getReportComplexOverview(this.complexId, this.startDateKey, this.thisDateKey)).data
          this.apiData.errors = (await ReportsApi.getReportComplexErrors(this.complexId, this.startDateKey, this.thisDateKey)).data
        } else {
          this.apiData.overview = (await ReportsApi.getReportRegionOverview(this.regionId, this.startDateKey, this.thisDateKey)).data
          this.apiData.errors = (await ReportsApi.getReportRegionErrors(this.regionId, this.startDateKey, this.thisDateKey)).data
        }
        getReportOverviewHasData = this.apiData.overview != null && this.apiData.overview.length > 0
        getReportErrorsHasData = this.apiData.errors != null && this.apiData.errors.length > 0

        await this.formatOverviewData()
        await this.formatErrorsData()
        this.setGraphs()
      } catch (e) {
        this.$notify({
          group: 'alerts',
          type: 'error',
          title: this.$t('views.switch.update.error'),
          text: '' + e,
          duration: 1000,
          speed: 1000
        })
        this.noData = true
      } finally {
        this.chartDataLoading = false
        this.reportHasData = getReportOverviewHasData || getReportErrorsHasData
      }
    },
    async setGraphs () {
      this.setOverviewGraph()
      this.setErrorsGraph()
      this.setAnomaliesGraph()
      this.setTop3Graph()
    },
    getHex (rgb) {
      let hex = Number(rgb).toString(16)
      if (hex.length < 2) {
        hex = '0' + hex
      }
      return hex
    },
    getColorString (r, g, b) {
      let red = this.getHex(r)
      let green = this.getHex(g)
      let blue = this.getHex(b)
      return '#' + red + green + blue
    },
    formatOverviewData () {
      let formatted = {}
      let ratioTopList = []
      let errorsTopList = []
      this.apiData.overview.forEach(x => {
        if (Object.keys(formatted).indexOf(x.DateKey + '') === -1) {
          formatted[x.DateKey] = []
        }
        formatted[x.DateKey].push({
          dateKey: x.DateKey,
          id: x.LocationId,
          name: x.LocationName,
          omlopen: x.omlopen,
          alarms: x.alarmen
        })
        let complex = ComplexService.getComplexByComplexId(x.ComplexId)
        if (x.DateKey + '' === this.thisDateKey) {
          ratioTopList.push({
            id: x.LocationId,
            name: x.LocationName,
            ratio: x.alarmen / x.omlopen,
            complexName: complex ? complex.Name : ''
          })
          errorsTopList.push({
            id: x.LocationId,
            name: x.LocationName,
            alarms: x.alarmen,
            complexName: complex ? complex.Name : ''
          })
        }
      })
      // fill up empty combinations of dateKey/locationId
      this.apiData.overview.forEach(x => {
        Object.keys(formatted).forEach(f => {
          let object = formatted[f].find(a => a.id === x.LocationId)
          if (!object) {
            formatted[f].push({
              dateKey: x.DateKey,
              id: x.LocationId,
              name: x.LocationName,
              omlopen: 0,
              alarms: 0
            })
          }
        })
      })
      let top = parseInt(this.apiData.params.top)
      ratioTopList = ratioTopList.sort((a, b) => b.ratio - a.ratio).splice(0, top)
      for (var i = 0; i < ratioTopList.length - 1; i++) {
        for (var j = i + 1; j < ratioTopList.length; j++) {
          var indi = ratioTopList[i].name.indexOf(' ')
          var indj = ratioTopList[j].name.indexOf(' ')
          if (indi > 0 && indj > 0) {
            if (ratioTopList[j].name.substring(0, indj).includes(ratioTopList[i].name.substring(0, indi))) {
              this.move(ratioTopList, j, i + 1)
            }
          }
        }
      }
      errorsTopList = errorsTopList.sort((a, b) => b.alarms - a.alarms).splice(0, top)

      // set to state
      this.apiData.overviewFormatted = formatted
      this.apiData.ratioTopList = ratioTopList
      this.apiData.errorsTopList = errorsTopList
    },
    formatErrorsData () {
      let formatted = {}
      let anomaliesTopList = []
      let warnings = []
      this.apiData.errors.forEach(x => {
        if (Object.keys(formatted).indexOf(x.Datepart + '') === -1) {
          formatted[x.Datepart] = []
        }
        let index = formatted[x.Datepart].findIndex(f => f.id === x.RailObjectId)
        if (index === -1) {
          index = formatted[x.Datepart].push({
            dateKey: x.Datepart,
            id: x.RailObjectId,
            name: x.Name,
            alarms: 0,
            warnings: 0
          }) - 1
        }

        // combine alarms and warnings
        if (x.SeverityId === 3) {
          formatted[x.Datepart][index].alarms = x.aantal
        } else {
          formatted[x.Datepart][index].warnings = x.aantal
        }

        let complex = ComplexService.getComplexByComplexId(x.ComplexId)
        // fill topList
        if (x.Datepart + '' === this.thisDateKey && x.SeverityId === 3) {
          anomaliesTopList.push({
            id: x.RailObjectId,
            name: x.Name,
            aantal: x.aantal,
            complexName: complex ? complex.Name : ''
          })
        }
        if (x.DateKey + '' === this.thisDateKey && x.SeverityId === 2) {
          warnings.push({
            id: x.RailObjectId,
            name: x.Name,
            aantal: x.aantal,
            complexName: complex ? complex.Name : ''
          })
        }
      })
      warnings = warnings.sort((a, b) => b.aantal - a.aantal)
      anomaliesTopList = anomaliesTopList.sort((a, b) => b.aantal - a.aantal)
      warnings.forEach(x => {
        if (anomaliesTopList.indexOf(x) === -1) {
          anomaliesTopList.push(x)
        }
      })
      let anomaliesTop = parseInt(this.apiData.params.top)
      anomaliesTopList = anomaliesTopList.splice(0, anomaliesTop)

      // set to state
      this.apiData.errorsFormatted = formatted
      this.apiData.anomaliesTopList = anomaliesTopList
    },
    async setOverviewGraph () {
      if (this.apiData.overviewFormatted !== []) {
        // process multiple dateKeys
        const formatted = this.apiData.overviewFormatted
        const ratioTopList = this.apiData.ratioTopList
        const dateKeyList = Object.keys(formatted)
        let sets = {}
        await dateKeyList.forEach(d => {
          sets[d] = {
            omlopen: [],
            alarms: []
          }
        })
        let categories = []
        ratioTopList.forEach(r => {
          categories.push(r.complexName + ' / ' + r.name)
        })

        dateKeyList.forEach(date => {
          ratioTopList.forEach(object => {
            let data = formatted[date].find(data => data.id === object.id)
            if (data !== null) {
              sets[date].omlopen.push(data.omlopen)
              sets[date].alarms.push(data.alarms)
            } else {
              sets[date].omlopen.push(0)
              sets[date].alarms.push(0)
            }
          })
        })

        let series = []
        dateKeyList.forEach(d => {
          const dateKey = parseInt(d)
          const index = dateKeyList.indexOf(dateKey + '')
          const numMonths = dateKeyList.length
          const fullMonth = this.fullMonths[parseInt(d.substring(4)) - 1]
          const padding = 0.05 // index * 0.1
          const colorSat = Math.floor((index + 1) * (255 / numMonths))
          const placement = 0 // ((Math.ceil((numMonths / 2) - 1)) - index) / numMonths
          series.push({
            name: this.$t('report.numberSwitches') + ' ' + fullMonth,
            marker: {
              enabled: false
            },
            data: sets[d].omlopen,
            groupPadding: padding,
            pointPlacement: placement,
            zIndex: index,
            color: this.getColorString(255 - colorSat, 255, 255 - colorSat) // green tint
          })
          series.push({
            name: this.$t('report.numberAlarms') + ' ' + fullMonth,
            marker: {
              enabled: false
            },
            data: sets[d].alarms,
            groupPadding: padding,
            pointPlacement: placement,
            zIndex: index,
            color: this.getColorString(255, 255 - colorSat, 255 - colorSat) // red tint
          })
        })

        this.charts.overview.xAxis.categories = categories
        this.charts.overview.series = series
        this.customTexts.overviewText = this.statsText()
      }
    },
    statsText () {
      let result = ''
      if (this.month !== null) {
        const selectedMonth = this.month.format('M') - 1
        const month = this.fullMonths[selectedMonth]
        const prevMonth = this.fullMonths[(selectedMonth + 11) % 12]

        const omlopen = this.textData.omlopen
        const alarms = this.textData.alarms
        const prevOmlopen = this.textData.prevOmlopen
        const prevAlarms = this.textData.prevAlarms
        if (this.apiData.params.numMonths > 1) {
          result = this.$t('report.statsText.plusPrev', [month, omlopen, prevOmlopen, prevMonth, alarms, prevAlarms])
        } else if (this.apiData.params.numMonths === 1) {
          result = this.$t('reports.statsText.justThis', [month, omlopen, alarms])
        }
      }
      return result
    },
    setErrorsGraph () {
      if (this.apiData.overviewFormatted !== []) {
        const formatted = this.apiData.overviewFormatted
        const errorsTopList = this.apiData.errorsTopList
        const dateKeyList = Object.keys(formatted)

        let sets = {}
        dateKeyList.forEach(d => {
          sets[d] = []
        })
        let categories = []
        errorsTopList.forEach(r => {
          categories.push(r.complexName + ' / ' + r.name)
        })

        dateKeyList.forEach(date => {
          errorsTopList.forEach(object => {
            let data = formatted[date].find(data => data.id === object.id)
            if (data !== null) {
              sets[date].push(data.alarms)
            } else {
              sets[date].push(0)
            }
          })
        })

        let series = []
        dateKeyList.forEach(d => {
          const dateKey = parseInt(d)
          const index = dateKeyList.indexOf(dateKey + '')
          const numMonths = dateKeyList.length
          const fullMonth = this.fullMonths[parseInt(d.substring(4)) - 1]
          const colorSat = Math.floor((index + 1) * (255 / numMonths))
          series.push({
            name: this.$t('report.numberAlarms') + ' ' + fullMonth,
            marker: {
              enabled: false
            },
            data: sets[d],
            groupPadding: 0.05,
            pointPlacement: 0,
            zIndex: 1,
            color: this.getColorString(255, 255 - colorSat, 255 - colorSat) // red tint
          })
        })

        this.charts.errors.xAxis.categories = categories
        this.charts.errors.series = series
      }
    },
    setAnomaliesGraph () {
      if (this.apiData.errorsFormatted !== []) {
        const formatted = this.apiData.errorsFormatted
        const anomaliesTopList = this.apiData.anomaliesTopList
        const dateKeyList = Object.keys(formatted)

        let sets = {}
        dateKeyList.forEach(d => {
          sets[d] = {
            warnings: [],
            alarms: []
          }
        })
        let categories = []
        anomaliesTopList.forEach(r => {
          categories.push(r.complexName + ' / ' + r.name)
        })

        dateKeyList.forEach(date => {
          anomaliesTopList.forEach(object => {
            let data = formatted[date].find(data => data.id === object.id)
            if (data !== undefined) {
              sets[parseInt(date)].alarms.push(data.alarms)
              sets[parseInt(date)].warnings.push(data.warnings)
            } else {
              sets[parseInt(date)].alarms.push(0)
              sets[parseInt(date)].warnings.push(0)
            }
          })
        })

        let series = []
        dateKeyList.forEach(d => {
          if (d === this.thisDateKey) {
            const fullMonth = this.fullMonths[parseInt(d.substring(4)) - 1]
            series.push({
              name: this.$t('report.numberWarnings') + ' ' + fullMonth,
              marker: {
                enabled: false
              },
              data: sets[d].warnings,
              groupPadding: 0.05,
              pointPlacement: 0,
              zIndex: 1,
              color: this.getColorString(255, 255, 0) // yellow tint
            })
            series.push({
              name: this.$t('report.numberAlarms') + ' ' + fullMonth,
              marker: {
                enabled: false
              },
              data: sets[d].alarms,
              groupPadding: 0.1,
              pointPlacement: 0,
              zIndex: 1,
              color: this.getColorString(255, 0, 0) // red tint
            })
          }
        })
        this.charts.anomaly.xAxis.categories = categories
        this.charts.anomaly.series = series
      }
    },
    setTop3Graph () {
      if (this.apiData.errorsFormatted !== []) {
        const formatted = this.apiData.errorsFormatted
        const anomaliesTopList = this.apiData.anomaliesTopList.splice(0, this.apiData.params.top3)
        const dateKeyList = Object.keys(formatted)

        let sets = {}
        dateKeyList.forEach(d => {
          sets[d] = {
            warnings: [],
            alarms: []
          }
        })
        let categories = []
        anomaliesTopList.forEach(r => {
          categories.push(r.complexName + ' / ' + r.name)
        })

        dateKeyList.forEach(date => {
          anomaliesTopList.forEach(object => {
            let data = formatted[date].find(data => data.id === object.id)
            if (data !== undefined) {
              sets[parseInt(date)].alarms.push(data.alarms)
              sets[parseInt(date)].warnings.push(data.warnings)
            } else {
              sets[parseInt(date)].alarms.push(0)
              sets[parseInt(date)].warnings.push(0)
            }
          })
        })

        let series = []
        dateKeyList.forEach(d => {
          const dateKey = parseInt(d)
          const index = dateKeyList.indexOf(dateKey + '')
          const numMonths = dateKeyList.length
          const fullMonth = this.fullMonths[parseInt(d.substring(4)) - 1]
          const padding = 0.05 // index * 0.05
          const colorSat = Math.floor((index + 1) * (255 / numMonths))
          const placement = 0 // ((Math.ceil((numMonths / 2) - 1)) - index) / numMonths
          series.push({
            name: this.$t('report.numberWarnings') + ' ' + fullMonth,
            marker: {
              enabled: false
            },
            data: sets[d].warnings,
            groupPadding: padding,
            pointPlacement: placement,
            zIndex: index,
            color: this.getColorString(255, 255, 255 - colorSat) // yellow tint
          })
          series.push({
            name: this.$t('report.numberAlarms') + ' ' + fullMonth,
            marker: {
              enabled: false
            },
            data: sets[d].alarms,
            groupPadding: padding,
            pointPlacement: 0,
            zIndex: index,
            color: this.getColorString(255, 255 - colorSat, 255 - colorSat) // red tint
          })
        })
        this.charts.top3.xAxis.categories = categories
        this.charts.top3.series = series
      }
    },
    addCustomPage () {
      this.customTexts.customPages.push({
        title: this.$t('report.title'),
        text: ' '
      })
    },
    removeCustomPage (index) {
      this.customTexts.customPages.splice(index, 1)
    },
    move (arr, oldIndex, newIndex) {
      while (oldIndex < 0) {
        oldIndex += arr.length
      }
      while (newIndex < 0) {
        newIndex += arr.length
      }
      if (newIndex >= arr.length) {
        var k = newIndex - arr.length
        while ((k--) + 1) {
          arr.push(undefined)
        }
      }
      arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0])
      return arr
    }
  },
  computed: {
    regions () {
      let regions = RegionService.getRegions()
      regions = regions.sort((a, b) => (a.Name >= b.Name ? 1 : -1))
      return regions
    },
    regionName: function () {
      if (this.regionId) {
        const region = RegionService.getRegionByRegionId(this.regionId)
        return region.Name
      } else {
        return ''
      }
    },
    complexes () {
      if (this.regionId) {
        let departmentsIds = DepartmentService.getDepartmentsByRegionId(this.regionId).map(d => d.DepartmentId)

        let allComplexes = ComplexService.getComplexes()
        let complexes = allComplexes.filter(c => departmentsIds.indexOf(c.DepartmentId) !== -1)
        return complexes
      }
      return []
    },
    thisDateKey: function () {
      const selectedMonth = parseInt(this.month.format('MM'))
      const selectedYear = parseInt(this.month.format('YYYY'))
      return selectedYear + '' + selectedMonth.toString().padStart(2, '0')
    },
    startDateKey: function () {
      const numMonths = this.apiData.params.numMonths
      const selectedMonth = parseInt(this.month.format('MM'))
      const selectedYear = parseInt(this.month.format('YYYY'))
      return (selectedMonth < numMonths ? selectedYear - 1 : selectedYear) + '' + ((selectedMonth + 12 - numMonths) % 12 + 1).toString().padStart(2, '0')
    },
    prevDateKey: function () {
      const selectedMonth = parseInt(this.month.format('MM'))
      const selectedYear = parseInt(this.month.format('YYYY'))
      return (selectedMonth <= 1 ? selectedYear - 1 : selectedYear) + '' + ((selectedMonth + 10) % 12 + 1).toString().padStart(2, '0')
    },
    textData: function () {
      const thisDateKey = parseInt(this.thisDateKey)
      const prevDateKey = parseInt(this.prevDateKey)
      let omlopen = 0
      let alarms = 0
      let prevOmlopen = 0
      let prevAlarms = 0

      this.apiData.overview.forEach(d => {
        if (d.DateKey === thisDateKey) {
          omlopen += d.omlopen
          alarms += d.alarmen
        }
        if (d.DateKey === prevDateKey) {
          prevOmlopen += d.omlopen
          prevAlarms += d.alarmen
        }
      })
      return {
        omlopen,
        alarms,
        prevOmlopen,
        prevAlarms
      }
    },
    thisMonth: function () {
      return moment()
    }
  },
  mounted () {
    this.regionId = this.regions[0].RegionId
  },
  watch: {
    month: function () {
      // this.getChartData()
      // this.getSVGData()
    },
    'apiData.params.numMonths': function () {
      // this.getChartData()
    },
    'apiData.params.top': function () {
      // this.getChartData()
    },
    'apiData.params.top3': function () {
      // this.getChartData()
    },
    regionId: function () {
      this.complexId = null
    },
    'currentUser': {
      immediate: true,
      handler: function (val, oldVal) {
        if (this.currentUser !== null) {
          switch (this.currentUser.OrganisationId) {
            case 46: // Friedrich Hippe
            case 81: // KVB
              this.creator = 'Rail Predict'
              this.imageBase64Data = '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'
              break

            // Default Strukton
            default:
              this.creator = 'Strukton Control Centre, Strukton Rail Nederland BV'
              this.imageBase64Data = `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`
              break
          }
        }
      }
    }
  }
}
</script>

<style scoped>
  #reports {
    width: 100%;
    height: calc(100vh - 56px);
    overflow-y: scroll;
    overflow-x: hidden;
  }

  .edit {
    background-color: #56CCF2;
    padding: 15px;
    margin-bottom: 0px;
    margin-top: 15px;
  }

  .edit span {
    font-weight: bold;
    line-height: 35px;
  }

  .edit .select {
    background-color: white;
  }

  .graph {
    padding: 50px 10px;
  }

  .buttonbg {
    margin-top: 20px;
    background: #56CCF2;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12);
    border-radius: 2px;
  }

  .chart-outer {
    max-width: 800px;
    margin: 2em auto;
  }

  #container {
    height: 300px;
    margin-top: 2em;
    min-width: 380px;
  }

  .highcharts-data-table table {
    border-collapse: collapse;
    border-spacing: 0;
    background: white;
    min-width: 100%;
    margin-top: 10px;
    font-family: sans-serif;
    font-size: 0.9em;
  }

  .highcharts-data-table td, .highcharts-data-table th, .highcharts-data-table caption {
    border: 1px solid silver;
    padding: 0.5em;
  }

  .highcharts-data-table tr:nth-child(even), .highcharts-data-table thead tr {
    background: #f8f8f8;
  }

  .highcharts-data-table tr:hover {
    background: #eff;
  }

  .highcharts-data-table caption {
    border-bottom: none;
    font-size: 1.1em;
    font-weight: bold;
  }
</style>



// WEBPACK FOOTER //
// src/pages/switch/SwitchReports.vue